import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import dagreD3 from 'dagre-d3';

const TYPE_LABELS = {
  mc_question: 'Multiple choice',
  open_question: 'Open question',
  slider: 'Slider',
  matrix: 'Matrix',
  message: 'Message',
  video: 'Video',
  iframe: 'Iframe',
  camera: 'Photo',
  video_camera: 'Video recording',
  voice: 'Voice',
  distributor: 'Distributor',
  unknown: 'Question'
};

export const typeLabel = type => TYPE_LABELS[type] || type;

const truncate = (value, max) => {
  if (!value) return '';
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
};

// A short, single-line summary of the answer for a node.
const answerHint = item => {
  switch (item.type) {
    case 'mc_question': {
      const selected = (item.options || [])
        .filter(option => option.selected)
        .map(option => option.text);
      let hint = selected.join(', ');
      if (item.otherAnswer) {
        hint = hint ? `${hint} — ${item.otherAnswer}` : item.otherAnswer;
      }
      return hint || '—';
    }
    case 'open_question':
      return item.openAnswer || '—';
    case 'slider':
      return item.sliderValue != null ? `value: ${item.sliderValue}` : '—';
    case 'matrix':
      return 'matrix answered';
    case 'camera':
    case 'video_camera':
    case 'voice':
      return item.mediaAnswerUrl ? 'media uploaded' : '—';
    default:
      return 'viewed';
  }
};

// What the question offers, for the graph of a survey that has no answers.
const structureHint = item => {
  switch (item.type) {
    case 'mc_question': {
      const options = item.options || [];
      if (!options.length) return 'no options';
      const branching = options.filter(option => option.kind === 'branch')
        .length;
      const label = `${options.length} option${
        options.length === 1 ? '' : 's'
      }`;
      return branching ? `${label} · ${branching} branching` : label;
    }
    case 'slider':
      return item.sliderMin != null && item.sliderMax != null
        ? `scale ${item.sliderMin}–${item.sliderMax}`
        : '';
    default:
      return '';
  }
};

const nodeStyles = (visited, structureOnly, outsideFlow) => {
  if (outsideFlow) {
    return 'fill:#fdf8ee;stroke:#b8860b;stroke-width:1px;stroke-dasharray:4,3';
  }
  if (structureOnly) return 'fill:#f7fafd;stroke:#1f6fb2;stroke-width:1.2px';
  return visited
    ? 'fill:#eaf3fb;stroke:#1f6fb2;stroke-width:1.5px'
    : 'fill:#f5f5f5;stroke:#cccccc;stroke-width:1px;stroke-dasharray:4,3';
};

const edgeStyle = edge => {
  if (edge.taken) {
    return {
      style: 'stroke:#1f6fb2;stroke-width:2.5px;fill:none',
      arrowheadStyle: 'fill:#1f6fb2;stroke:#1f6fb2'
    };
  }
  if (edge.kind === 'jump') {
    return {
      style: 'stroke:#e08a1e;stroke-width:1.3px;fill:none;stroke-dasharray:5,4',
      arrowheadStyle: 'fill:#e08a1e;stroke:#e08a1e'
    };
  }
  if (edge.kind === 'end') {
    return {
      style: 'stroke:#b8860b;stroke-width:1.3px;fill:none;stroke-dasharray:4,4',
      arrowheadStyle: 'fill:#b8860b;stroke:#b8860b'
    };
  }
  return {
    style: 'stroke:#c4c4c4;stroke-width:1.2px;fill:none',
    arrowheadStyle: 'fill:#c4c4c4;stroke:#c4c4c4'
  };
};

function LegendItem({ color, dashed, label }) {
  return <span style={legendStyles.item}>
    <span
      style={{
        ...legendStyles.swatch,
        borderTop: `3px ${dashed ? 'dashed' : 'solid'} ${color}`
      }}
    />
    {label}
  </span>
}

function ResultFlowGraph({ items, graphEdges, groups, structureOnly }) {
  const containerRef = useRef(null);
  const svgRef = useRef(null);
  const innerRef = useRef(null);

  useEffect(
    () => {
      if (!svgRef.current || !innerRef.current || !items || !items.length) {
        return;
      }

      const edges = graphEdges || [];
      const groupList = groups || [];

      // Questions that content.flows does not contain are no part of the
      // survey structure, so the graph leaves them out. One that the user did
      // answer is kept: it is part of what happened, and it is marked as
      // outside the flow. A survey that keeps its whole flow outside
      // content.flows (firstFlow + per-question nextFlow) is drawn as it is.
      const structureItems = items.filter(
        item => item.inStructure !== false || item.visited
      );
      const nodeItems = structureItems.length ? structureItems : items;
      const innerSelection = d3.select(innerRef.current);
      innerSelection.selectAll('*').remove();

      const graph = new dagreD3.graphlib.Graph({ compound: true })
        .setGraph({
          rankdir: 'TB',
          marginx: 16,
          marginy: 16,
          ranksep: 36,
          nodesep: 24,
          edgesep: 10
        })
        .setDefaultEdgeLabel(() => ({}));

      // Question nodes (distributors are routers — drawn as neutral diamonds).
      nodeItems.forEach(item => {
        if (item.type === 'distributor') {
          graph.setNode(item.questionId, {
            label: truncate(item.questionText, 26) || 'Distributor',
            shape: 'diamond',
            style: 'fill:#ffffff;stroke:#9b9b9b;stroke-width:1px',
            labelStyle: 'fill:#555;font-size:11px'
          });
          return;
        }
        const stepPrefix =
          item.visited && item.order != null ? `#${item.order + 1} · ` : '';
        // A question of the survey that content.flows does not contain: it
        // belongs to the survey but no path of the survey leads to it
        const outsideFlow = item.inStructure === false;
        let hint;
        if (outsideFlow) {
          hint = '(not in the survey flow)';
        } else if (structureOnly) {
          hint = structureHint(item);
        } else {
          hint = item.visited
            ? truncate(answerHint(item), 44)
            : '(not reached)';
        }
        const lines = [
          `${stepPrefix}${typeLabel(item.type)}`,
          truncate(item.questionText, 44)
        ];
        if (hint) lines.push(hint);
        graph.setNode(item.questionId, {
          label: lines.join('\n'),
          style: nodeStyles(item.visited, structureOnly, outsideFlow),
          labelStyle:
            item.visited || structureOnly || outsideFlow
              ? 'fill:#173a56;font-size:12px'
              : 'fill:#999999;font-size:12px'
        });
      });

      // Terminal nodes.
      if (edges.some(edge => edge.to === '__success__')) {
        graph.setNode('__success__', {
          label: 'End survey',
          shape: 'ellipse',
          style: 'fill:#d8f3df;stroke:#1e7e34;stroke-width:1.5px',
          labelStyle: 'fill:#1e7e34;font-weight:bold;font-size:12px'
        });
      }
      if (edges.some(edge => edge.to === '__failure__')) {
        graph.setNode('__failure__', {
          label: 'Survey failed',
          shape: 'ellipse',
          style: 'fill:#ffe7c2;stroke:#b8860b;stroke-width:1.5px',
          labelStyle: 'fill:#8a4b00;font-weight:bold;font-size:12px'
        });
      }

      // Group clusters.
      groupList.forEach(group => {
        graph.setNode(group.id, {
          label: truncate(group.label, 24),
          clusterLabelPos: 'top',
          style: 'fill:#eceef6;stroke:#c3c8de;stroke-width:1px'
        });
      });
      nodeItems.forEach(item => {
        if (
          item.groupId &&
          graph.hasNode(item.groupId) &&
          graph.hasNode(item.questionId)
        ) {
          graph.setParent(item.questionId, item.groupId);
        }
      });
      groupList.forEach(group => {
        if (group.parentId && graph.hasNode(group.parentId)) {
          graph.setParent(group.id, group.parentId);
        }
      });

      // Edges.
      edges.forEach(edge => {
        if (!graph.hasNode(edge.from) || !graph.hasNode(edge.to)) return;
        const styling = edgeStyle(edge);
        graph.setEdge(edge.from, edge.to, {
          label: edge.choiceLabel ? truncate(edge.choiceLabel, 22) : '',
          labelStyle: 'fill:#888;font-size:10px',
          curve: d3.curveBasis,
          ...styling
        });
      });

      graph.nodes().forEach(name => {
        const node = graph.node(name);
        if (node) {
          node.rx = 6;
          node.ry = 6;
        }
      });

      const svgSelection = d3.select(svgRef.current);
      const render = new dagreD3.render();
      render(innerSelection, graph);

      // Nudge cluster labels above their boxes.
      svgSelection
        .selectAll('g.clusters g.cluster g.label')
        .attr('transform', 'translate(0, -12)');

      const containerWidth =
        (containerRef.current && containerRef.current.offsetWidth) || 760;
      const graphWidth = graph.graph().width || containerWidth;
      const graphHeight = graph.graph().height || 320;
      const scale = Math.min(1, (containerWidth - 24) / graphWidth);
      const translateX = Math.max(
        12,
        (containerWidth - graphWidth * scale) / 2
      );

      const zoom = d3.zoom().on('zoom', () => {
        innerSelection.attr('transform', d3.event.transform);
      });
      svgSelection.call(zoom);
      svgSelection.call(
        zoom.transform,
        d3.zoomIdentity.translate(translateX, 12).scale(scale)
      );
      svgSelection.attr('height', Math.min(680, graphHeight * scale + 28));
    },
    [items, graphEdges, groups, structureOnly]
  );

  if (!items || !items.length) {
    return null;
  }

  return (
    <div ref={containerRef} style={styles.container}>
      <svg ref={svgRef} height={480} style={styles.svg}>
        <g ref={innerRef} />
      </svg>
      <div style={styles.legend}>
        {!structureOnly && <LegendItem color="#1f6fb2" label="Path taken" />}
        <LegendItem
          color="#e08a1e"
          dashed
          label={structureOnly ? 'Branch / jump' : 'Branch / jump (not taken)'}
        />
        <LegendItem color="#c4c4c4" label="Default order" />
        {structureOnly && (
          <LegendItem color="#b8860b" dashed label="End of survey" />
        )}
        <span style={legendStyles.item}>Scroll to zoom · drag to pan</span>
      </div>
    </div>
  );
}

const styles = {
  container: {
    width: '100%'
  },
  svg: {
    width: '100%',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    background: '#fff'
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginTop: '8px',
    fontSize: '12px',
    color: '#777'
  }
};

const legendStyles = {
  item: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  swatch: {
    display: 'inline-block',
    width: '22px',
    height: '0px'
  }
};

export default ResultFlowGraph;
